// watchVideoTool.js — "watch a video" agent tool (agent-computer-use E4). Exposes
// the Electron watch backend (yt-dlp captions + ffmpeg frames) to the model via the
// extraTools seam. The model gets a timestamped transcript it can summarize AND
// translate to any language (translation is native to the LLM — the tool just
// hands over the text). Read-only + safe (network fetch of a user-given URL + temp
// files), so it's available whenever the desktop bridge exists (no toggle).

const TRANSCRIPT_CAP = 12_000;

export const WATCH_VIDEO_DEF = {
  type: 'function',
  function: {
    name: 'watch_video',
    description:
      'Assiste um vídeo (YouTube/URL/arquivo local) e devolve a TRANSCRIÇÃO com timestamps, mais título e duração. '
      + 'Use para resumir, responder perguntas sobre, ou TRADUZIR um vídeo — depois de receber a transcrição, traduza o texto para o idioma pedido. '
      + 'Passe frames>0 (ex.: 6) para também extrair quadros do vídeo (salvos em disco; úteis quando o áudio não basta). Só funciona no app desktop.',
    parameters: {
      type: 'object',
      properties: {
        url: { type: 'string', description: 'URL do vídeo (YouTube etc.) ou caminho de arquivo local' },
        frames: { type: 'number', description: 'quantos quadros extrair (0-12, padrão 0 = só transcrição, mais rápido)' },
        translate_to: { type: 'string', description: 'idioma-alvo (opcional) — você mesmo traduz a transcrição retornada' },
      },
      required: ['url'],
    },
  },
};

/** Compact, model-facing STRING for a watch_video result. Pure; never throws. */
export function formatWatchResult(data, { translateTo } = {}) {
  const d = data && typeof data === 'object' ? data : {};
  if (!d.ok) return `Erro watch_video: ${d.reason || 'falhou'}.`;
  const head = [
    `Vídeo: ${d.title || '(sem título)'}`,
    Number.isFinite(d.duration) && d.duration ? `Duração: ${Math.round(d.duration)}s` : '',
    d.caption_source && d.caption_source !== 'none' ? `Legendas: ${d.caption_source}` : '',
    Array.isArray(d.frames) && d.frames.length ? `Frames: ${d.frames.length} (${d.frames.slice(0, 2).join(', ')}…)` : '',
  ].filter(Boolean).join(' · ');
  const transcript = String(d.transcript || '').slice(0, TRANSCRIPT_CAP);
  const body = transcript
    ? `\n\nTranscrição${transcript.length >= TRANSCRIPT_CAP ? ' (truncada)' : ''}:\n${transcript}`
    : `\n\n${d.note || 'Sem transcrição disponível para este vídeo.'}`;
  const tail = translateTo && transcript ? `\n\n➡️ Traduza a transcrição acima para: ${translateTo}.` : '';
  return head + body + tail;
}

const bridge = () => {
  const w = /** @type {any} */ (typeof window !== 'undefined' ? window : {});
  return w?.apolo?.watchVideo || null;
};

/** Build the {def, execute} tool for the extraTools seam. Read-only ⇒ not mutating. */
export function makeWatchVideoTool() {
  return {
    def: WATCH_VIDEO_DEF,
    execute: async (args = {}, ctx = {}) => {
      const cu = bridge();
      if (!cu) return 'Erro watch_video: indisponível (fora do app desktop).';
      const url = String(args?.url || '').trim();
      if (!url) return 'Erro watch_video: url obrigatório.';
      try {
        const data = await cu.run({ url, frames: Number(args?.frames) || 0 });
        // Feed the extracted frames to a vision model via the loop's image hook.
        if (data?.ok && Array.isArray(data.frameImages) && data.frameImages.length && typeof ctx.attachImages === 'function') {
          ctx.attachImages(data.frameImages);
        }
        return formatWatchResult(data, { translateTo: args?.translate_to });
      } catch (e) {
        return `Erro watch_video: ${e?.message || e}`;
      }
    },
  };
}

/** Gated resolver: [] unless the desktop bridge exists (web build has no watch backend). */
export function resolveWatchVideoTools() {
  return bridge() ? [makeWatchVideoTool()] : [];
}
